import { useEffect } from 'react';
import { useThemeStore, applyTheme, activeCustomTheme } from '../lib/stores';
import {
  openThemePreviewChannel, applyCustomTokens, setThemePreviewing, type ThemePreviewMessage,
} from '../lib/customTheme';

// If the editor window is hard-closed (no 'stop' is delivered — BroadcastChannel
// has no close event), revert once its heartbeats stop arriving.
const STALE_MS = 6000;

/**
 * Live cross-window preview receiver, mounted in the app Layout. The theme-editor
 * window broadcasts the in-progress theme; this applies it to <html> WITHOUT
 * persisting so the running app previews edits instantly. Reverts on 'stop', on
 * 'saved' (after syncing this window's store), or when the editor goes silent.
 */
export function ThemePreviewListener() {
  useEffect(() => {
    const ch = openThemePreviewChannel();
    if (!ch) return;
    let timer: number | null = null;

    const revertToSaved = () => {
      if (timer) { clearTimeout(timer); timer = null; }
      setThemePreviewing(false);
      const s = useThemeStore.getState();
      applyTheme(s.palette, s.mode, s.contrast, activeCustomTheme(s));
    };
    const armStaleTimer = () => {
      if (timer) clearTimeout(timer);
      timer = window.setTimeout(revertToSaved, STALE_MS);
    };

    ch.onmessage = (e: MessageEvent<ThemePreviewMessage>) => {
      const msg = e.data;
      if (!msg) return;
      if (msg.type === 'preview') {
        setThemePreviewing(true);
        const root = document.documentElement;
        root.setAttribute('data-theme', 'custom');
        root.setAttribute('data-mode', msg.mode);
        if (msg.contrast === 'high') root.setAttribute('data-contrast', 'high');
        else root.removeAttribute('data-contrast');
        applyCustomTokens(msg.theme, msg.mode, msg.contrast, root);
        armStaleTimer();
      } else if (msg.type === 'heartbeat') {
        if (timer) armStaleTimer(); // refresh only while a preview is in flight
      } else if (msg.type === 'saved') {
        // Editor committed. Cross-window stores don't auto-share, so sync THIS
        // window's store to the saved theme + selection, then apply it.
        const st = useThemeStore.getState();
        st.upsertCustomTheme(msg.theme);
        st.selectCustom(msg.activeCustomId);
        if (timer) { clearTimeout(timer); timer = null; }
        setThemePreviewing(false);
        const s2 = useThemeStore.getState();
        applyTheme(s2.palette, s2.mode, s2.contrast, activeCustomTheme(s2));
      } else if (msg.type === 'stop') {
        revertToSaved();
      }
    };

    return () => {
      if (timer) clearTimeout(timer);
      ch.close();
      revertToSaved(); // harmless re-apply if no preview was active
    };
  }, []);

  return null;
}
